import React, { useRef, useEffect, useState } from 'react'
import Card from '../../components/Card.jsx'
import * as api from '../../services/api.js'

const HOBBIES = [
  { name: 'Watercolour',    desc: 'Paint something in front of you with just three colours.',                 tags: ['creative','quiet','messy'] },
  { name: 'Wild foraging',  desc: 'Look up one edible plant local to you. Find it on a walk.',               tags: ['outdoors','nature','curious'] },
  { name: 'Journaling',     desc: 'Write three pages of whatever — no rules, no re-reading.',                 tags: ['reflective','writing','solo'] },
  { name: 'Bread baking',   desc: 'Make the simplest loaf recipe you can find. Eat it warm.',                 tags: ['kitchen','tactile','slow'] },
  { name: 'Bird watching',  desc: 'Sit outside for 20 mins and count how many birds you see.',                tags: ['outdoors','patient','quiet'] },
  { name: 'Collage',        desc: 'Cut pictures from old magazines. Make something ugly on purpose.',          tags: ['creative','playful','hands'] },
  { name: 'Film photography', desc: 'Shoot one roll on a disposable camera — no previewing.',                 tags: ['creative','slow','analogue'] },
  { name: 'Pottery',        desc: 'Find a local throw-and-fire class. Touch clay.',                           tags: ['tactile','social','creative'] },
  { name: 'Astronomy',      desc: 'Download a star app. Go outside after dark and look up.',                  tags: ['quiet','science','wonder'] },
  { name: 'Letter writing', desc: 'Write a real letter to someone you miss. Post it.',                        tags: ['connection','slow','kind'] },
  { name: 'Knitting',       desc: 'Learn to cast on. Make something five stitches wide.',                     tags: ['hands','meditative','cosy'] },
  { name: 'Sketching',      desc: 'Draw one object near you for 15 minutes without lifting your pen.',        tags: ['creative','focus','quiet'] },
  { name: 'Herb growing',   desc: 'Buy one small pot of herbs. Put it on a windowsill.',                      tags: ['growing','nature','home'] },
  { name: 'Podcast walk',   desc: 'Pick a curious podcast and walk somewhere new while listening.',            tags: ['learning','moving','solo'] },
  { name: 'Wild swimming',  desc: 'Find a river or lido. Go in slowly.',                                      tags: ['brave','body','outdoors'] },
  { name: 'Cold-water swim',desc: 'Find your nearest open water. Just dip your feet first.',                  tags: ['brave','body','outdoors'] },
]

const WHEEL_COLOURS = ['#238b45','#52A96A','#2E7D3E','#7BBFA0','#1a6b34','#A8C96A','#8FBD8F','#74C49A']

function drawWheel(canvas, highlight) {
  if (!canvas) return
  const ctx = canvas.getContext('2d')
  const cx = 100, cy = 100, r = 96
  const n = HOBBIES.length
  const arc = (2 * Math.PI) / n
  ctx.clearRect(0, 0, 200, 200)

  HOBBIES.forEach((h, i) => {
    const start = i * arc - Math.PI / 2
    ctx.beginPath()
    ctx.moveTo(cx, cy)
    ctx.arc(cx, cy, r, start, start + arc)
    ctx.closePath()
    ctx.fillStyle = i === highlight ? '#0D2B12' : WHEEL_COLOURS[i % WHEEL_COLOURS.length]
    ctx.fill()
    ctx.strokeStyle = '#EEF5EE'
    ctx.lineWidth = 1.5
    ctx.stroke()

    ctx.save()
    ctx.translate(cx, cy)
    ctx.rotate(start + arc / 2)
    ctx.textAlign = 'right'
    ctx.fillStyle = 'rgba(255,255,255,0.9)'
    ctx.font = '500 9px DM Sans, sans-serif'
    ctx.fillText(h.name, r - 6, 3)
    ctx.restore()
  })

  // Centre circle
  ctx.beginPath()
  ctx.arc(cx, cy, 28, 0, 2 * Math.PI)
  ctx.fillStyle = 'white'
  ctx.fill()
  ctx.strokeStyle = '#8FBD8F'
  ctx.lineWidth = 1
  ctx.stroke()
}

export default function HobbySpinner() {
  const canvasRef              = useRef(null)
  const [spinning, setSpinning] = useState(false)
  const [current, setCurrent]  = useState(null)
  const [tried, setTried]      = useState([])

  useEffect(() => { api.getHobbies().then(d => setTried(d || [])) }, [])

  useEffect(() => {
    drawWheel(canvasRef.current, null)
  }, [])

  function spin() {
    if (spinning) return
    setSpinning(true)
    setCurrent(null)

    const picked = Math.floor(Math.random() * HOBBIES.length)
    const totalDeg = (4 + Math.floor(Math.random() * 4)) * 360 + (picked / HOBBIES.length) * 360
    const canvas = canvasRef.current
    canvas.style.transition = `transform ${2 + Math.random()}s cubic-bezier(0.17,0.67,0.12,1)`
    canvas.style.transform  = `rotate(${totalDeg}deg)`

    setTimeout(() => {
      setSpinning(false)
      setCurrent(picked)
      drawWheel(canvas, picked)
      canvas.style.transition = ''
      canvas.style.transform  = ''
    }, 3200)
  }

  function markTried() {
    if (current === null) return
    const name = HOBBIES[current].name
    if (!tried.includes(name)) {
      const next = [...tried, name]
      setTried(next)
      api.saveHobbies(next)
    }
  }

  const hobby = current !== null ? HOBBIES[current] : null
  const alreadyTried = hobby ? tried.includes(hobby.name) : false

  return (
    <Card label="Hobby spinner" title="Discover something new to try">
      <div className="spinner-wrap">
        <div className="wheel-container">
          <canvas ref={canvasRef} width="200" height="200" />
          <button className="spin-btn-big" onClick={spin} disabled={spinning}>
            {spinning ? '...' : 'Spin'}
          </button>
        </div>

        <div className="hobby-result">
          {!hobby && !spinning && (
            <p style={{ fontSize: '0.85rem', color: 'var(--stone)', fontStyle: 'italic' }}>
              Spin to discover something new to try
            </p>
          )}
          {spinning && (
            <p style={{ fontSize: '0.85rem', color: 'var(--stone)', fontStyle: 'italic' }}>
              Spinning...
            </p>
          )}
          {hobby && !spinning && (
            <>
              <p className="hobby-name">{hobby.name}</p>
              <p className="hobby-desc">{hobby.desc}</p>
            </>
          )}
        </div>

        {hobby && !spinning && (
          <div id="hobby-tags" style={{ textAlign: 'center' }}>
            {hobby.tags.map(t => (
              <span key={t} className="hobby-tag">{t}</span>
            ))}
          </div>
        )}

        {hobby && !spinning && (
          <button
            className="save-btn ready"
            onClick={markTried}
            style={{ fontSize: '0.78rem', padding: '6px 14px', opacity: alreadyTried ? 0.5 : 1 }}
          >
            {alreadyTried ? 'Tried this already ✓' : 'I tried this ✓'}
          </button>
        )}

        {tried.length > 0 && (
          <p style={{ fontSize: '0.82rem', color: 'var(--stone)', fontStyle: 'italic', textAlign: 'center' }}>
            {tried.length} hobby{tried.length > 1 ? 's' : ''} tried so far
          </p>
        )}
      </div>
    </Card>
  )
}
